/**
 * TextElement — F12.H T12.H.5.
 *
 * Tekst akapitowy (`<p>`) jako element dekoracyjny/instrukcyjny w formularzu.
 * Używany do dodawania opisów, instrukcji, pomocniczego tekstu między polami.
 *
 * Settings shape:
 *   { text: string, size?: 'sm'|'md'|'lg', muted?: boolean, align?: 'left'|'center'|'right' }
 */

import { Dropdown } from '@/components/Dropdown';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

type TextSize = 'sm' | 'md' | 'lg';
type TextAlign = 'left' | 'center' | 'right';

const SIZE_CLASSES: Record<TextSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

const ALIGN_CLASSES: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const TextElement: LayoutElementDefinition = {
  type: 'text',
  category: 'tresc',
  isContainer: false,
  label: 'Tekst',
  icon: 'Type',
  defaultSettings: () => ({
    text: 'Treść akapitu',
    size: 'md',
    muted: false,
    align: 'left',
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const text = String(settings.text ?? '');
    const size = (settings.size as TextSize) ?? 'md';
    const align = (settings.align as TextAlign) ?? 'left';
    const muted = settings.muted === true;
    return (
      <div className="space-y-3" data-testid="text-properties-editor">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Treść</label>
          <textarea
            value={text}
            onChange={(e) => onChange({ text: e.target.value })}
            rows={4}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            data-testid="text-prop-text"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">Rozmiar</label>
            <Dropdown
              value={size}
              onChange={(v) => onChange({ size: v as TextSize })}
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
              data-testid="text-prop-size"
              options={[
                { value: 'sm', label: 'Mały' },
                { value: 'md', label: 'Normalny' },
                { value: 'lg', label: 'Duży' },
              ]}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">Wyrównanie</label>
            <Dropdown
              value={align}
              onChange={(v) => onChange({ align: v as TextAlign })}
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
              data-testid="text-prop-align"
              options={[
                { value: 'left', label: 'Do lewej' },
                { value: 'center', label: 'Wyśrodkowane' },
                { value: 'right', label: 'Do prawej' },
              ]}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={muted}
            onChange={(e) => onChange({ muted: e.target.checked })}
            className="h-4 w-4 rounded border-border-primary text-accent-600"
            data-testid="text-prop-muted"
          />
          <span className="text-text-secondary">Przygaszony (mniej kontrastu)</span>
        </label>
      </div>
    );
  },
  render: ({ element, mode }) => {
    const settings = (element.settings ?? {}) as {
      text?: string;
      size?: TextSize;
      muted?: boolean;
      align?: TextAlign;
    };
    const size = settings.size ?? 'md';
    const align = settings.align ?? 'left';
    const colorClass = settings.muted ? 'text-text-tertiary' : 'text-text-secondary';
    const text = settings.text || (mode === 'builder' ? '(pusty tekst)' : '');

    return (
      <p
        className={`${SIZE_CLASSES[size]} ${ALIGN_CLASSES[align]} ${colorClass} whitespace-pre-wrap`}
        data-testid={`text-element-${element.id}`}
        data-element-type="text"
      >
        {text}
      </p>
    );
  },
};
